import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  Input,
} from '@angular/core';
import { ModalController } from '@ionic/angular';

import { StripeService } from '../../../services/stripe.service';
import { UserService } from '../../../services/user.service';
import { Utilities } from '../../../utilities/utilities';
import { ClientService } from '../../../services/client.service';
import { MaskitoOptions, MaskitoElementPredicateAsync } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ENV_CONFIG } from '../../../../bin/env.config';

declare var Stripe: stripe.StripeStatic;
@Component({
  selector: 'smr-stripe-manual-card-payment',
  templateUrl: './stripe-manual-card-payment.component.html',
  styleUrls: ['./stripe-manual-card-payment.component.scss'],
})
export class StripeManualCardPaymentComponent implements OnInit {
  @ViewChild('cardElement') cardElement: ElementRef;

  public stripe;

  @Input() cardOnFile;

  public card;

  public cardInfo = {
    brand: '',
    last4: '',
    exp_month: '',
    exp_year: '',
    funding: '',
    paymentMethodID: '',
  };

  public paymentMethodType;

  public client;

  public cardErrors;

  public confirmation;

  @Input() sale;

  public currency;

  public chargePushed: boolean = false;

  public chargeAndSavePushed: boolean = false;

  public errorMessage;

  public paymentIntent;

  public chargeSavedCardPushed: boolean = false;

  public clientFirstName: string;

  public clientLastName: string;

  currencyMask: MaskitoOptions;

  readonly maskPredicate: MaskitoElementPredicateAsync = async (el) =>
    (el as HTMLIonInputElement).getInputElement();

  public paymentField: FormControl;

  public paymentForm: FormGroup;

  public amountInput: number;

  public paymentMethodID: string;

  public style = {
    base: {
      color: '#32325D',
      fontWeight: 500,
      fontFamily: 'Source Code Pro, Consolas, Menlo, monospace',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      '::placeholder': {
        color: '#CFD7DF',
      },
      ':-webkit-autofill': {
        color: '#e39f48',
      },
    },
    invalid: {
      color: '#E25950',

      '::placeholder': {
        color: '#FFCCA5',
      },
    },
  };

  constructor(
    private modalCtrl: ModalController,
    private stripeService: StripeService,
    private ref: ChangeDetectorRef,
    public userService: UserService,
    private clientService: ClientService,
    private formBuilder: FormBuilder
  ) {


    this.currencyMask = maskitoNumberOptionsGenerator({
      decimalZeroPadding: true,
      precision: 2,
      decimalSeparator: '.',
      thousandSeparator: ',',
      min: 0,
      prefix: Utilities.decodeHTML(this.userService.getSalon().getCurrency())
    });

  }

  public updatePayment(event) {
    this.amountInput = Utilities.extractNumberFromString(
      this.paymentField.value
    );
  }

  public async ngOnInit() {
    const balance = () => this.sale.balance;
    this.amountInput = balance();
    this.paymentField = new FormControl(
      Utilities.roundTo2Decimal(this.amountInput).toFixed(2),
      Validators.compose([Validators.required])
    );
    this.paymentForm = this.formBuilder.group({
      payment: this.paymentField,
    });
    this.currency = Utilities.decodeHtml(
      this.userService.getSalon().getCurrency()
    );
    this.client = this.sale.getClient();
  }

  public ngAfterViewInit() {
    this.stripe = Stripe(ENV_CONFIG.stripePublicKey);
    const elements = this.stripe.elements();

    if (!this.cardOnFile) {
      // client doesn't have card on file
      this.card = elements.create('card', { style: this.style });
      this.card.mount(this.cardElement.nativeElement);
      this.card.addEventListener('change', ({ error }) => {
        this.cardErrors = error && error.message;
      });
    } else {
      // client has card on file
      this.stripeService
        .getCustomerCardInfo({ clientID: this.client.id })
        .subscribe((cardInfo) => {
          this.cardInfo = cardInfo;
          this.paymentMethodID = this.cardInfo.paymentMethodID;
          this.ref.detectChanges();
        });
    }
  }

  public async chargeCardWithEntry(saveClient) {
    this.chargePushed = !saveClient;
    this.chargeAndSavePushed = saveClient;
    this.errorMessage = false;
    const { error } = await this.stripe.createSource(this.card);

    if (error) {
      this.errorMessage = error.message;
      // make the charge button active again
      this.resetChargeButtons();
      this.ref.detectChanges();
    } else {
      const country = this.userService.getSalon().getCountry();
      const currency = country.countryName === 'Canada' ? 'cad' : 'usd';
      const rawAmount = Math.round(this.amountInput * 100);
      this.stripeService
        .createPaymentIntentWithCard({
          rawAmount,
          currency,
          saveClient,
          clientID: this.client.id,
          paymentType: 'card',
        })
        .subscribe({
          next: (paymentIntent) => {
            this.paymentIntent = paymentIntent;
            this.payWithCard(this.paymentIntent.client_secret)
              .then(() => { })
              .catch((error) => {
              });
          },
          error: (error) => {
            // issue is here
            // make the charge button active again
            this.errorMessage = error.message;
            this.resetChargeButtons();
            this.ref.detectChanges();
          },
        });
    }
  }

  public getAmount() {
    return this.amountInput;
  }

  public resetChargeButtons() {
    this.chargeSavedCardPushed = false;
    this.chargePushed = false;
    this.chargeAndSavePushed = false;
  }

  public payWithCard = async (clientSecret) => {
    this.stripe
      .confirmCardPayment(clientSecret, {
        payment_method: {
          card: this.card,
        },
      })
      .catch((error) => {
      })
      .then((result) => {
        if (result.error) {
          // Show error to your customer
          let code = result.error.code;
          let decline_code = result.error.decline_code;
          if (code === 'card_declined') {
            let declinedInformation =
              this.stripeService.getDeclinedInformation(decline_code);
            this.errorMessage = declinedInformation.description;
          } else {
            this.errorMessage = result.error.message;
          }
          // make the charge button active again
          this.resetChargeButtons();
          this.ref.detectChanges();
        } else {
          if (result.paymentIntent.status === 'succeeded') {
            // The payment succeeded
            this.stripeService
              .getPaymentMethod({
                paymentMethodID: result.paymentIntent.payment_method,
              })
              .subscribe((paymentMethod) => {
                this.paymentMethodType = paymentMethod;
                const paymentType = this.stripeService.stripePaymentType(
                  this.paymentMethodType.card.brand,
                  this.paymentMethodType.card.funding
                );
                this.modalCtrl.dismiss({
                  amount: result.paymentIntent.amount / 100,
                  type: paymentType.type,
                  name: paymentType.name,
                  intentID: result.id,
                  last4Digits: this.paymentMethodType.card.last4,
                  expDate:
                    ('0' + this.paymentMethodType.card.exp_month).slice(-2) +
                    '/' +
                    (this.paymentMethodType.card.exp_year + '').slice(-2),
                  applicationFee: Math.round(((result.paymentIntent.amount * 29) / 1000) + 30) / 100
                });
              });
          } else {
            // charge didn't complete
            // eg. capture required
            this.errorMessage =
              'We were unable to process your payment. Please try again or use another card.';
            this.resetChargeButtons();
            this.ref.detectChanges();
          }
        }
      });
  };

  public async chargeSavedCard() {
    this.chargeSavedCardPushed = true;
    const country = this.userService.getSalon().getCountry();
    const currency = country.countryName === 'Canada' ? 'cad' : 'usd';
    const paymentAmountInCents = Math.round(this.getAmount() * 100);
    this.stripeService
      .chargeSavedCard({
        currency,
        amount: paymentAmountInCents,
        clientID: this.client.id,
      })
      .subscribe({
        next: (result) => {
          console.log(result);
          this.resetChargeButtons();
          const paymentType = this.stripeService.stripePaymentType(
            result.payment_method_details.card.brand,
            result.payment_method_details.card.funding
          );
          this.modalCtrl.dismiss({
            amount: result.amount_captured / 100,
            type: paymentType.type,
            name: paymentType.name,
            intentID: result.payment_intent,
            last4Digits: result.payment_method_details.card.last4,
            expDate:
              ('0' + result.payment_method_details.card.exp_month).slice(-2) +
              '/' +
              (result.payment_method_details.card.exp_year + '').slice(-2),
            chargeID: result.id,
            applicationFee: Math.round(((result.amount_captured * 29) / 1000) + 30) / 100
          });
        },
        error: (error) => {
          let code = error.code;
          let decline_code = error.decline_code;
          if (code === 'card_declined') {
            let declinedInformation =
              this.stripeService.getDeclinedInformation(decline_code);
            this.errorMessage = declinedInformation.description;
          }
          // make the charge button active again
          this.resetChargeButtons();
          this.ref.detectChanges();
        },
      });
  }

  public onDismiss() {
    this.modalCtrl.dismiss();
  }
}
